/**
 * Blog posts for FloAI. Used by /blog (list) and /blog/[slug] (single post).
 * To add a post: duplicate an entry below, set slug (URL-safe), title, excerpt, date, and body.
 * Ongoing blog content and publishing owned by Marketing (Renee).
 */

export type BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  date: string; // YYYY-MM-DD
  body: string;
};

export const posts: BlogPost[] = [
  {
    slug: 'welcome-to-the-floai-blog',
    title: 'Welcome to the FloAI Blog',
    excerpt: 'Introducing the FloAI blog: tips, product updates, and stories to help creators grow.',
    date: '2026-03-17',
    body: `We're excited to launch the FloAI blog. Here you'll find product updates, how-to guides, and ideas to help you create, schedule, and grow your presence across social platforms.

FloAI Studio is built for creators who want one place to plan content, connect channels, and use tools like the Social Media Calculator, Hashtag Research, and AI-powered content creation. This blog will support that journey with practical advice and news.

What you can expect: short how-tos, feature spotlights, and best practices for posting, engagement, and using FloAI's tools. We'll also share company updates and the occasional creator story.

Thanks for being here. If you have ideas for topics or questions, reach out via our contact page or support.`,
  },
  {
    slug: 'how-to-use-the-social-media-calculator',
    title: 'How to Use the Social Media Calculator',
    excerpt: 'Estimate the value and ROI of your social presence in minutes with FloAI\'s Social Media Calculator.',
    date: '2026-03-17',
    body: `The Social Media Calculator helps you put a number on the time and value of your social presence. Whether you're reporting to a client or planning your own strategy, it's a quick way to get a baseline.

Open the tool from the Tools menu or by going to the Social Media Calculator page. Enter your follower counts and engagement metrics for each platform you use. If you have existing data in a spreadsheet, you can often import it via CSV to save time.

The calculator estimates reach, engagement value, and time spent. Use these numbers to compare channels, set goals, or show stakeholders the impact of your work. Many users then run a Social Media Audit for deeper recommendations.

Tip: Update your numbers periodically so your baselines stay relevant.`,
  },
  {
    slug: 'getting-the-most-from-hashtag-research',
    title: 'Getting the Most from Hashtag Research',
    excerpt: 'Find the right hashtags for reach and relevance using FloAI\'s Hashtag Research tool.',
    date: '2026-03-17',
    body: `Good hashtags can extend your reach and connect you with the right audience. FloAI's Hashtag Research tool is built to help you discover and compare hashtags quickly.

Start by entering a topic or a seed hashtag you already use. The tool suggests related tags and shows metrics so you can balance popularity with relevance. Mix broader and niche hashtags for better results.

Once you've picked a set, add them to your content from the tool or copy them into the Create page. You can also use the AI suggestions to get caption ideas that fit your chosen hashtags.

Use Hashtag Research when you're planning a campaign, testing a new niche, or refreshing your usual set. A few minutes here can improve discoverability for your next posts.`,
  },
  {
    slug: 'best-times-to-post-and-how-floai-can-help',
    title: 'Best Times to Post and How FloAI Can Help',
    excerpt: 'Why timing matters and how FloAI\'s scheduling and tools support better posting times.',
    date: '2026-03-17',
    body: `Posting when your audience is most active can improve reach and engagement. "Best times" vary by platform, niche, and your own audience, but a few principles hold: consistency and data beat guesswork.

FloAI helps in two ways. First, use the Content Calendar or Create page to schedule posts in advance. Plan a week or month at a time so you're not posting at random. Second, use our Optimal Posting Time feature (when available in your plan) to see when your followers are most engaged and align your schedule with those windows.

If you don't have analytics yet, start by posting at a few different times and days and note what performs. Over time, your FloAI dashboard and tools will help you refine.

Combine good timing with strong hashtags and clear messaging—FloAI's Hashtag Research and AI Content tools are there to support that.`,
  },
];

export function getPostBySlug(slug: string): BlogPost | undefined {
  return posts.find((p) => p.slug === slug);
}

export function getAllSlugs(): string[] {
  return posts.map((p) => p.slug);
}
